// EPIC-P05-07: Badge Criteria Editor Component
// JSON editor with schema validation for badge criteria

import { useState } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AdminCard } from '../primitives'
import { CheckCircle2, AlertTriangle, TestTube } from 'lucide-react'
import { evaluateCriteria } from '@/lib/api/admin/badges'
import { toast } from 'sonner'

interface BadgeCriteriaEditorProps {
  value: any
  onChange: (value: any) => void
}

export function BadgeCriteriaEditor({ value, onChange }: BadgeCriteriaEditorProps) {
  const [jsonText, setJsonText] = useState(() => JSON.stringify(value || {}, null, 2))
  const [isValid, setIsValid] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [testing, setTesting] = useState(false)

  const handleTextChange = (text: string) => {
    setJsonText(text)
    try {
      const parsed = JSON.parse(text)
      setIsValid(true)
      setError(null)
      onChange(parsed)
    } catch (e) {
      setIsValid(false)
      setError(e instanceof Error ? e.message : 'Invalid JSON')
    }
  }

  const handleTest = async () => {
    if (!isValid) {
      toast.error('Please fix JSON errors before testing')
      return
    }

    setTesting(true)
    try {
      const criteria = JSON.parse(jsonText)
      const result = await evaluateCriteria(criteria)
      
      if (result.passed) {
        toast.success('Criteria test passed!')
      } else {
        toast.error(`Criteria test failed: ${result.reason || 'Unknown reason'}`)
      }
    } catch (error) {
      toast.error(`Test error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setTesting(false)
    }
  }

  return (
    <AdminCard accentColor="info" title="Criteria Configuration">
      <div className="space-y-4">
        <div>
          <Label htmlFor="criteria-json">Criteria JSON</Label>
          <Textarea
            id="criteria-json"
            value={jsonText}
            onChange={(e) => handleTextChange(e.target.value)}
            className={`font-mono text-sm min-h-[200px] ${isValid ? '' : 'border-destructive'}`}
            placeholder='{"type": "count", "metric": "posts_count", "operator": ">=", "value": 1}'
          />
          {error && (
            <Alert variant="destructive" className="mt-2">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {isValid && jsonText && (
            <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span>Valid JSON</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            <p>Example formats:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Count: {'{"type": "count", "metric": "posts_count", "operator": ">=", "value": 1}'}</li>
              <li>Manual: {'{"manual_award": true}'}</li>
            </ul>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleTest}
            disabled={!isValid || testing}
          >
            <TestTube className="h-4 w-4 mr-2" />
            {testing ? 'Testing...' : 'Test Criteria'}
          </Button>
        </div>
      </div>
    </AdminCard>
  )
}

